import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Clock, Loader2 } from "lucide-react";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InsightArticle = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setArticle(null);
    setMissing(false);
    axios
      .get(`${API}/insights/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setMissing(true));
  }, [slug]);

  return (
    <main data-testid="insight-article-page" className="pt-32 sm:pt-40 pb-16 sm:pb-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/insights"
          data-testid="article-back-link"
          className="inline-flex items-center gap-2 font-mono-brand text-[11px] uppercase tracking-[0.18em] text-[#64748B] hover:text-[#0F382C] transition-colors"
        >
          <ArrowLeft size={14} /> All insights
        </Link>

        {!article && !missing && <Loader2 size={26} className="animate-spin text-[#0F382C] mx-auto mt-24" />}

        {missing && (
          <div className="mt-16 text-center" data-testid="article-not-found">
            <h1 className="font-display text-3xl font-semibold text-[#0B192C]">Article not found</h1>
            <p className="mt-3 text-sm text-[#64748B]">This insight may have moved — browse the full list instead.</p>
          </div>
        )}

        {article && (
          <article>
            <Seo title={`${article.title} | NCS Insights`} description={article.excerpt} />
            <Reveal>
              <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-2 pb-6 border-b border-[#0B192C]/10">
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.16em] text-[#0F382C] bg-[#0F382C]/[0.07] px-3 py-1.5">
                  {article.category}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-[#94A3B8]">
                  <Clock size={12} /> {article.read_time} min read
                </span>
                <span className="text-xs text-[#94A3B8]">
                  {new Date(article.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                </span>
              </div>
              <h1 data-testid="article-title" className="mt-8 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.12] text-[#0B192C]">
                {article.title}
              </h1>
              <p className="mt-5 font-display text-lg sm:text-xl italic text-[#475569] leading-relaxed">
                {article.excerpt}
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-10 space-y-6" data-testid="article-body">
                {article.content.map((block, i) =>
                  block.type === "h2" ? (
                    <h2 key={i} className="pt-6 font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#0B192C]">
                      <span className="inline-block w-6 h-px bg-[#C5A059] mr-4 align-middle" aria-hidden="true" />
                      {block.text}
                    </h2>
                  ) : (
                    <p key={i} className={`text-base sm:text-[17px] leading-[1.85] text-[#3F4A5A] ${i === 0 ? "dropcap" : ""}`}>
                      {block.text}
                    </p>
                  )
                )}
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-16 bg-[#0B192C] border-t-2 border-[#C5A059] p-8 sm:p-12 text-center" data-testid="article-cta">
                <p className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">Work with NCS</p>
                <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-white">
                  Want this working in your organisation?
                </h2>
                <p className="mt-3 text-sm text-white/60 max-w-md mx-auto">
                  Talk to our clinical services team about commissioning this kind of support.
                </p>
                <Link
                  to="/contact"
                  data-testid="article-cta-button"
                  className="group mt-7 inline-flex items-center gap-2.5 bg-[#C5A059] text-[#0B192C] font-semibold px-7 py-3.5 rounded-full text-sm transition-colors duration-300 hover:bg-white"
                >
                  Speak to Our Clinical Services Team
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </article>
        )}
      </div>
    </main>
  );
};

export default InsightArticle;
